/**
 * WebSocket Client for FIAS Platform Streaming
 *
 * Provides real-time token streaming for AI responses.
 * Handles connection management, reconnection, and message routing.
 */

/** Server → Client message types */
interface TokenMessage {
  type: 'token';
  nodeId: string;
  text: string;
  requestId: string;
}

interface ThinkingMessage {
  type: 'thinking';
  nodeId: string;
  text: string;
  requestId: string;
}

interface NodeCompleteMessage {
  type: 'node_complete';
  nodeId: string;
  requestId: string;
  /** Credits this node charged the user (after finalization); 1 credit = 1 US cent. */
  costCredits?: number;
}

interface CompleteMessage {
  type: 'complete';
  requestId: string;
  /** Extra payload fields (e.g., guidedResult) passed through to onComplete */
  [key: string]: unknown;
}

interface ErrorMessage {
  type: 'error';
  code: string;
  message: string;
  /**
   * Server-side broadcasters that scope errors to a specific request
   * (e.g. plugin `image_generate` failures) carry the originating
   * `requestId`. Consumers handling multiple in-flight requests on the
   * shared singleton MUST filter on this — without it, an error for
   * request B would reject request A's pending Promise too. Older
   * broadcasters that omit the field (legacy generic connection
   * errors) still reach every listener.
   */
  requestId?: string;
}

interface SubscribedMessage {
  type: 'subscribed';
  requestId: string;
}

interface UnsubscribedMessage {
  type: 'unsubscribed';
}

interface PongMessage {
  type: 'pong';
}

/** Image generation progress message */
interface ProgressMessage {
  type: 'progress';
  stage: string;
  message: string;
  requestId: string;
}

/** Image generation complete message */
interface ImageCompleteMessage {
  type: 'image_complete';
  requestId: string;
  imageRef: string;
  fileId?: string;
  revisedPrompt?: string;
  provider: string;
  model: string;
  /**
   * Present on plugin-bridge image_generate broadcasts (the host bridge
   * uses this to round-trip the full pre-refactor `ImageInvokeResult`
   * shape into the iframe). The internal arche image_generate route
   * omits it — its consumers (`useImageGeneratorSubmit`) fetch a fresh
   * presigned URL via `/image-url`. Optional to keep both producers
   * compatible with the same callback signature.
   */
  imageUrl?: string;
  /**
   * Credits this generation charged the user (after finalization); 1 credit =
   * 1 US cent. Populated by BOTH the internal arche image_generate route (so the
   * Image Generator can show per-image cost) and plugin-bridge broadcasts (where
   * the host forwards it into the iframe so the SDK's
   * `await fias.images.generate(...)` resolves with the `ImageInvokeResult`
   * shape byte-for-byte).
   */
  costCents?: number;
  /**
   * The user can't save in the workspace they're acting in, so the image went
   * to their Personal Holder's Arche Saves. Internal arche route only.
   */
  savedToPersonal?: boolean;
  /**
   * Deprecation note when the selector substituted a deprecated entity.
   * Carried on plugin-bridge broadcasts only.
   */
  deprecation?: {
    requestedEntityId: string;
    resolvedEntityId: string;
    deprecatedAt?: string;
  };
}

/**
 * Generic completion broadcast for the async-over-WebSocket *surface*
 * dispatch (`surface_invoke` with `asyncDispatch`). Unlike
 * `image_complete` (which carries a fixed `ImageInvokeResult` shape), the
 * surface result is opaque — each surface defines its own `resultSchema`,
 * so the payload rides in `result` and the host bridge forwards it into
 * the iframe verbatim. Mirrors the `image_complete` envelope otherwise.
 */
interface SurfaceCompleteMessage {
  type: 'surface_complete';
  requestId: string;
  /** The surface's validated result object (shape per its `resultSchema`). */
  result: unknown;
}

interface UiUpdateMessage {
  type: 'ui_update';
  nodeId: string;
  requestId: string;
  uiDefinition: unknown;
}

interface SettingsUpdateMessage {
  type: 'settings_update';
  requestId: string;
  settings: Record<string, unknown>;
}

interface FilesUpdateMessage {
  type: 'files_update';
  requestId: string;
  files: Record<string, unknown>;
}

/**
 * Always-on plain-English status line describing what the assistant is doing
 * right now (e.g. "Writing code…"). Ephemeral — rendered as a transient
 * indicator, never part of chat history.
 */
interface ActivityMessage {
  type: 'activity';
  requestId: string;
  label: string;
}

/** Verbose mode message types */
interface SystemPromptMessage {
  type: 'system_prompt';
  requestId: string;
  prompt: string;
}

/**
 * Two producers share these frames. The builders send `toolName` /
 * `toolInput` / `toolResult`; the Fias Forge runtime (ADR-023,
 * routes/forge/runtime.ts `frameForEvent`) sends Claude Code's own shape —
 * `toolUseId` + `tool` / `input` on the call, `toolUseId` + `output` on the
 * result. `handleMessage` normalizes both onto the one callback signature and
 * passes `toolUseId` through so a consumer can pair a result with its call.
 */
interface ToolCallMessage {
  type: 'tool_call';
  requestId: string;
  toolName?: string;
  toolInput?: unknown;
  toolUseId?: string;
  tool?: string;
  input?: unknown;
}

interface ToolResultMessage {
  type: 'tool_result';
  requestId: string;
  toolName?: string;
  toolResult?: unknown;
  isError: boolean;
  toolUseId?: string;
  output?: unknown;
}

/**
 * A screenshot taken inside a Fias Forge turn (ADR-023). The runtime's
 * Playwright MCP writes PNGs to `.forge/screenshots/`; the controller pushes
 * them as `screenshot` events and the platform rebroadcasts them on the
 * turn's requestId as this frame. `contentBase64` is a PNG (≤ 1 MB raw).
 */
interface ForgeScreenshotMessage {
  type: 'forge_screenshot';
  requestId: string;
  /** Workspace-relative path the runtime wrote the PNG to. */
  path: string;
  contentBase64: string;
}

/**
 * Claude asked the forge's owner a question (AskUserQuestion) and the Fias
 * Forge turn is waiting for the answer (ADR-023 A12). The page answers with
 * `POST /v1/forge/forges/:forgeId/turns/:turnId/answer`.
 */
interface ForgeQuestionMessage {
  type: 'forge_question';
  requestId: string;
  turnId: string;
  questionId: string;
  questions: Array<{
    question: string;
    header: string;
    options: Array<{ label: string; description: string }>;
    multiSelect: boolean;
  }>;
}

/**
 * Claude asked the forge's owner to approve its plan (ExitPlanMode) and the
 * Fias Forge turn is waiting for the decision (ADR-023 A14). The page decides
 * with `POST /v1/forge/forges/:forgeId/turns/:turnId/plan`.
 */
interface ForgePlanMessage {
  type: 'forge_plan';
  requestId: string;
  turnId: string;
  questionId: string;
  /** The plan, as markdown. */
  plan: string;
}

interface ModelInfoMessage {
  type: 'model_info';
  requestId: string;
  model: string;
  intent: string;
}

interface ToolsAvailableMessage {
  type: 'tools_available';
  requestId: string;
  tools: Array<{ name: string; description: string }>;
}

/**
 * Chat message types from server.
 * These are intentional duplicates of the canonical types in @fias/kv-types.
 * The client package cannot depend on the server-side kv-types package directly.
 * If modifying these, also update: packages/kv-types/src/websocket.ts
 */
interface ChatMessageEventData {
  type: 'chat_message';
  channelId: string;
  message: {
    messageId: string;
    senderId: string;
    senderName: string | null;
    content: string;
    messageType: string;
    replyToId: string | null;
    // The resolved quoted-message preview, so a reply renders as a reply on
    // the live feed without waiting for a refetch. Mirrors the GET payload.
    replyPreview?: { senderName: string; content: string } | null;
    attachmentRefs: Array<{
      fileId: string;
      fileName: string;
      mimeType: string;
      sizeBytes: number;
    }> | null;
    mentions: Array<{ userId: string; displayName: string }> | null;
    createdAt: string;
  };
}

interface ChatMessageEditedEventData {
  type: 'chat_message_edited';
  channelId: string;
  messageId: string;
  content: string;
  editedAt: string;
}

interface ChatMessageDeletedEventData {
  type: 'chat_message_deleted';
  channelId: string;
  messageId: string;
}

interface ChatTypingEventData {
  type: 'chat_typing';
  channelId: string;
  userId: string;
  displayName: string;
}

interface ChatReactionEventData {
  type: 'chat_reaction';
  channelId: string;
  messageId: string;
  userId: string;
  emoji: string;
  action: 'add' | 'remove';
}

/** Calendar provider sync applied remote changes to local calendars —
 * emitted by the worker after a sync run's terminal commit. */
interface CalendarSyncAppliedEventData {
  type: 'calendar-sync-applied';
  calendarIds: string[];
}

interface ChatPresenceEventData {
  type: 'chat_presence';
  userId: string;
  status: 'online' | 'offline' | 'away';
}

interface ChatChannelAddedEventData {
  type: 'chat_channel_added';
  channelId: string;
}

interface ChatSubscribedEventData {
  type: 'chat_subscribed';
  channelIds: string[];
}

/** Fias AI search results message */
interface SearchResultsMessage {
  type: 'search_results';
  requestId: string;
  results: unknown[];
  query: string;
}

/** Fias AI arche proposal message */
interface ArcheProposalMessage {
  type: 'arche_proposal';
  requestId: string;
  proposalId: string;
  actions: Array<{
    actionId: string;
    params: Record<string, unknown>;
    description: string;
  }>;
}

/**
 * Realtime topic subscription frames (ADR-012 Phase M4). Client-local
 * mirrors of the `@fias/kv-types` shapes (this package cannot depend on the
 * server-side types package — same posture as the chat messages above).
 */
interface TopicSubscribedMessage {
  type: 'topic_subscribed';
  topic: string;
  /** AUTHORITATIVE lease end (epoch ms) — renewal schedules from this. */
  leaseExpiresAt: number;
}

interface TopicSubscribeDeniedMessage {
  type: 'topic_subscribe_denied';
  topic: string;
  /** LIMIT = the caller's own caps, retryable; DENIED = everything else. */
  code: 'DENIED' | 'LIMIT';
}

interface SubscriptionEventMessage {
  type: 'subscription_event';
  topic: string;
  event: { collection: string; changed: true };
}

interface SubscriptionExpiredMessage {
  type: 'subscription_expired';
  topic: string;
}

interface TopicTerminatedMessage {
  type: 'topic_terminated';
  topic: string;
  reason: 'deleted';
}

/** Assistant agent-loop events (global Fias AI orchestrator) */
interface AssistantToolCallMessage {
  /** Always sent by the loop — the client filters events to the active request. */
  requestId: string;
  type: 'assistant_tool_call';
  toolCallId: string;
  toolName: string;
  description: string;
  riskTier: string;
  /** 'awaiting' = proposed but paused for the user's confirmation (not yet
   * executed) — its spinner must stop even though there's no result yet. */
  status: 'running' | 'done' | 'error' | 'awaiting';
  summary?: string;
  refs?: Array<{ id: string; type: string; label: string }>;
  domains?: string[];
}

interface AssistantQuestionMessage {
  /** Always sent by the loop — the client filters events to the active request. */
  requestId: string;
  type: 'assistant_question';
  interactionId: string;
  question: string;
  choices?: string[];
}

interface AssistantConfirmationRequiredMessage {
  /** Always sent by the loop — the client filters events to the active request. */
  requestId: string;
  type: 'assistant_confirmation_required';
  interactionId: string;
  /** The paused tool's call id (correlates to the 'awaiting' tool row). */
  toolCallId?: string;
  toolName: string;
  riskTier: string;
  reason: string;
  preview: {
    type: 'generic';
    title: string;
    fields: Array<{ label: string; value: string }>;
    /** Editable params — pre-filled inputs the user may change before approving. */
    editable?: Array<{
      key: string;
      label: string;
      input: 'text' | 'textarea' | 'email' | 'tel';
      value: string;
      placeholder?: string;
    }>;
    actingAs: string;
  };
}

interface AssistantUiActionMessage {
  /** Always sent by the loop — the client filters events to the active request. */
  requestId: string;
  type: 'assistant_ui_action';
  toolCallId: string;
  /** The arche the action MUST be applied in — refuse on mismatch. */
  archeId: string;
  actionId: string;
  params: Record<string, unknown>;
  description: string;
}

interface AssistantNavigateMessage {
  /** Always sent by the loop — the client filters events to the active request. */
  requestId: string;
  type: 'assistant_navigate';
  toolCallId: string;
  archeId: string;
  prefill?: Record<string, unknown>;
}

/**
 * Server liveness during a long request-scoped stream (the builder sends one
 * every 15 s). No payload: receiving it is the proof of life.
 */
interface HeartbeatMessage {
  type: 'heartbeat';
  requestId?: string;
}

type ServerMessage =
  | HeartbeatMessage
  | AssistantNavigateMessage
  | AssistantToolCallMessage
  | AssistantQuestionMessage
  | AssistantConfirmationRequiredMessage
  | AssistantUiActionMessage
  | TokenMessage
  | ThinkingMessage
  | NodeCompleteMessage
  | CompleteMessage
  | ErrorMessage
  | SubscribedMessage
  | UnsubscribedMessage
  | PongMessage
  | ProgressMessage
  | ImageCompleteMessage
  | SurfaceCompleteMessage
  | UiUpdateMessage
  | SettingsUpdateMessage
  | FilesUpdateMessage
  | ActivityMessage
  | SystemPromptMessage
  | ToolCallMessage
  | ToolResultMessage
  | ForgeScreenshotMessage
  | ForgeQuestionMessage
  | ForgePlanMessage
  | ModelInfoMessage
  | ToolsAvailableMessage
  | ChatMessageEventData
  | ChatMessageEditedEventData
  | ChatMessageDeletedEventData
  | ChatTypingEventData
  | ChatReactionEventData
  | ChatPresenceEventData
  | ChatChannelAddedEventData
  | ChatSubscribedEventData
  | CalendarSyncAppliedEventData
  | SearchResultsMessage
  | ArcheProposalMessage
  | TopicSubscribedMessage
  | TopicSubscribeDeniedMessage
  | SubscriptionEventMessage
  | SubscriptionExpiredMessage
  | TopicTerminatedMessage;

/** Client → Server message types */
interface SubscribeClientMessage {
  type: 'subscribe';
  requestId: string;
}

interface UnsubscribeClientMessage {
  type: 'unsubscribe';
  /**
   * Optional — when present, only that subscription is dropped on the
   * server. When absent (legacy single-sub behavior), the connection
   * is unsubscribed from ALL its current requestIds. Mirrors
   * `UnsubscribeMessage` in `@fias/kv-types`; kept in sync because the
   * client package cannot depend on the server-side types package.
   */
  requestId?: string;
}

interface PingClientMessage {
  type: 'ping';
}

interface ChatSubscribeClientMessage {
  type: 'chat_subscribe';
  channelIds: string[];
}

interface ChatUnsubscribeClientMessage {
  type: 'chat_unsubscribe';
  channelIds: string[];
}

interface ChatTypingClientMessage {
  type: 'chat_typing';
  channelId: string;
}

interface ChatReadClientMessage {
  type: 'chat_read';
  channelId: string;
  messageId: string;
}

interface TopicSubscribeClientMessage {
  type: 'topic_subscribe';
  topic: string;
  /** Single-use grant from a subscribe-kind entity verb (fias_sgr_…). */
  grant: string;
}

interface TopicUnsubscribeClientMessage {
  type: 'topic_unsubscribe';
  topic: string;
}

type ClientMessage =
  | SubscribeClientMessage
  | UnsubscribeClientMessage
  | PingClientMessage
  | ChatSubscribeClientMessage
  | ChatUnsubscribeClientMessage
  | ChatTypingClientMessage
  | ChatReadClientMessage
  | TopicSubscribeClientMessage
  | TopicUnsubscribeClientMessage;

/** Connection state */
type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/** Event callbacks */
type TokenCallback = (nodeId: string, text: string, requestId: string) => void;
type ThinkingCallback = (nodeId: string, text: string, requestId: string) => void;
type NodeCompleteCallback = (nodeId: string, requestId: string, costCredits?: number) => void;
type RequestCompleteCallback = (requestId: string, payload?: Record<string, unknown>) => void;
type ErrorCallback = (code: string, message: string, requestId?: string) => void;
type ConnectionChangeCallback = (state: ConnectionState) => void;
type SubscriptionCallback = (requestId: string) => void;
type ProgressCallback = (stage: string, message: string, requestId: string) => void;
type ImageCompleteCallback = (data: {
  requestId: string;
  imageRef: string;
  fileId?: string;
  revisedPrompt?: string;
  provider: string;
  model: string;
  /** See ImageCompleteMessage.imageUrl — plugin-bridge path only. */
  imageUrl?: string;
  /** See ImageCompleteMessage.costCents — credits charged (1 credit = 1 US cent). */
  costCents?: number;
  /** See ImageCompleteMessage.savedToPersonal. */
  savedToPersonal?: boolean;
  /** See ImageCompleteMessage.deprecation — plugin-bridge path only. */
  deprecation?: {
    requestedEntityId: string;
    resolvedEntityId: string;
    deprecatedAt?: string;
  };
}) => void;
type SurfaceCompleteCallback = (data: { requestId: string; result: unknown }) => void;
// Realtime topic subscriptions (ADR-012 Phase M4)
type TopicSubscribedCallback = (topic: string, leaseExpiresAt: number) => void;
type TopicSubscribeDeniedCallback = (topic: string, code: 'DENIED' | 'LIMIT') => void;
type SubscriptionEventCallback = (
  topic: string,
  event: { collection: string; changed: true },
) => void;
type SubscriptionExpiredCallback = (topic: string) => void;
type TopicTerminatedCallback = (topic: string) => void;
type UiUpdateCallback = (nodeId: string, uiDefinition: unknown, requestId: string) => void;
type SettingsUpdateCallback = (settings: Record<string, unknown>, requestId: string) => void;
type FilesUpdateCallback = (files: Record<string, unknown>, requestId: string) => void;
type SystemPromptCallback = (prompt: string, requestId: string) => void;
type ActivityCallback = (label: string, requestId: string) => void;
type ToolCallCallback = (
  toolName: string,
  toolInput: unknown,
  requestId: string,
  /** Forge frames only: Claude Code's tool_use id, for pairing with the result. */
  toolUseId?: string,
) => void;
type ToolResultCallback = (
  toolName: string,
  toolResult: unknown,
  isError: boolean,
  requestId: string,
  /** Forge frames only — the result carries no tool name, just this id. */
  toolUseId?: string,
) => void;
export type ForgeScreenshotEventPayload = Omit<ForgeScreenshotMessage, 'type'>;
type ForgeScreenshotCallback = (payload: ForgeScreenshotEventPayload) => void;
export type ForgeQuestionEventPayload = Omit<ForgeQuestionMessage, 'type'>;
type ForgeQuestionCallback = (payload: ForgeQuestionEventPayload) => void;
export type ForgePlanEventPayload = Omit<ForgePlanMessage, 'type'>;
type ForgePlanCallback = (payload: ForgePlanEventPayload) => void;
type ModelInfoCallback = (model: string, intent: string, requestId: string) => void;
type ToolsAvailableCallback = (
  tools: Array<{ name: string; description: string }>,
  requestId: string,
) => void;

/** Chat event callbacks */
type ChatMessageCallback = (channelId: string, message: ChatMessageEventData['message']) => void;
type ChatMessageEditedCallback = (
  channelId: string,
  messageId: string,
  content: string,
  editedAt: string,
) => void;
type ChatMessageDeletedCallback = (channelId: string, messageId: string) => void;
type ChatTypingCallback = (channelId: string, userId: string, displayName: string) => void;
type ChatReactionCallback = (
  channelId: string,
  messageId: string,
  userId: string,
  emoji: string,
  action: 'add' | 'remove',
) => void;
type ChatPresenceCallback = (userId: string, status: 'online' | 'offline' | 'away') => void;
type ChatChannelAddedCallback = (channelId: string) => void;
type CalendarSyncAppliedCallback = (calendarIds: string[]) => void;

/** Fias AI event callbacks */
type SearchResultsCallback = (results: unknown[], query: string, requestId: string) => void;
export type AssistantToolCallEventPayload = Omit<AssistantToolCallMessage, 'type'>;
export type AssistantQuestionEventPayload = Omit<AssistantQuestionMessage, 'type'>;
export type AssistantConfirmationEventPayload = Omit<AssistantConfirmationRequiredMessage, 'type'>;
export type AssistantUiActionEventPayload = Omit<AssistantUiActionMessage, 'type'>;
export type AssistantNavigateEventPayload = Omit<AssistantNavigateMessage, 'type'>;
type AssistantToolCallCallback = (payload: AssistantToolCallEventPayload) => void;
type AssistantQuestionCallback = (payload: AssistantQuestionEventPayload) => void;
type AssistantConfirmationCallback = (payload: AssistantConfirmationEventPayload) => void;
type AssistantUiActionCallback = (payload: AssistantUiActionEventPayload) => void;
type AssistantNavigateCallback = (payload: AssistantNavigateEventPayload) => void;
type ArcheProposalCallback = (
  proposal: {
    proposalId: string;
    actions: Array<{ actionId: string; params: Record<string, unknown>; description: string }>;
  },
  requestId: string,
) => void;

interface WebSocketConfig {
  /** WebSocket endpoint URL (without protocol) */
  endpoint: string;
  /** Maximum reconnection attempts */
  maxReconnectAttempts?: number;
  /** Base reconnection delay in ms */
  reconnectDelayMs?: number;
  /** Heartbeat interval in ms */
  heartbeatIntervalMs?: number;
}

const DEFAULT_MAX_RECONNECT_ATTEMPTS = 5;
const DEFAULT_RECONNECT_DELAY_MS = 1000;
const DEFAULT_HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Thrown by `doConnect()` when a connection attempt is abandoned mid-flight
 * because a newer attempt or an explicit `disconnect()` superseded it (the
 * connection "generation" advanced while we awaited the ws-ticket). It is a
 * deliberate takeover, NOT a connection failure: callers and the reconnect
 * timer must treat it as a no-op (do not re-arm backoff, do not surface it as
 * a CONNECTION_FAILED), unlike a real ticket/handshake error.
 */
export class ConnectionSupersededError extends Error {
  constructor() {
    super('Connection attempt superseded');
    this.name = 'ConnectionSupersededError';
  }
}

/**
 * Message used when `disconnect()` rejects an in-flight handshake it just tore
 * down. Exported so consumers can recognise this benign abort without string
 * matching a literal in two places.
 */
export const WS_DISCONNECTED_BEFORE_CONNECTING = 'WebSocket disconnected before connecting';

/**
 * True for connect() rejections that are INTENTIONAL lifecycle teardowns — a
 * newer attempt or an explicit `disconnect()` superseded the in-flight
 * handshake — as opposed to a genuine failure to reach the server (bad ticket,
 * socket error, ws-ticket endpoint outage). The client's own reconnect path
 * (or the next `connect()`) recovers from a benign abort, so consumers MUST NOT
 * surface it as a user-facing error. This is the documented contract on
 * {@link ConnectionSupersededError}; surfacing it as CONNECTION_FAILED was what
 * made the first chat prompt after a cold/cycled socket render a spurious
 * "An error occurred with this AI provider".
 */
export function isBenignConnectAbort(err: unknown): boolean {
  return (
    err instanceof ConnectionSupersededError ||
    (err instanceof Error && err.message === WS_DISCONNECTED_BEFORE_CONNECTING)
  );
}

/**
 * FIAS WebSocket Client
 *
 * Usage:
 * ```typescript
 * const ws = new FiasWebSocket({ endpoint: 'wss://api.example.com/stream' });
 *
 * // Set up event handlers
 * ws.onToken((nodeId, text) => console.log(`${nodeId}: ${text}`));
 * ws.onNodeComplete((nodeId) => console.log(`Node ${nodeId} complete`));
 * ws.onComplete((requestId) => console.log(`Request ${requestId} complete`));
 *
 * // Connect with a ticket provider — fresh single-use ticket per (re)connect
 * await ws.connect(() => authApi.getWebSocketTicket());
 *
 * // Subscribe to a request
 * ws.subscribe(requestId);
 *
 * // Later, disconnect
 * ws.disconnect();
 * ```
 */
export class FiasWebSocket {
  private config: Required<WebSocketConfig>;
  private socket: WebSocket | null = null;
  private state: ConnectionState = 'disconnected';
  // Provider is invoked on every (re)connect inside `doConnect()` so
  // each connection gets a fresh single-use ws-ticket. Required because
  // ws-tickets are consumed atomically on connect — replaying one is a
  // hard fail.
  private tokenProvider: (() => Promise<string | null>) | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  /**
   * Set once the backoff has burned through `maxReconnectAttempts` and the
   * terminal CONNECTION_FAILED has been reported. Makes `scheduleReconnect()`
   * idempotent at the terminal: an abnormal close at the last attempt fires
   * the error from `onclose`, and the reconnect timer's catch would otherwise
   * re-enter `scheduleReconnect()` (state is 'disconnected', not
   * 'reconnecting') and report it a SECOND time. Cleared by `stopReconnect()`
   * (i.e. on any explicit connect / successful open / disconnect), so a later
   * connect() can start a fresh chain.
   */
  private reconnectExhausted = false;
  /**
   * The promise of the connection attempt currently in flight (state
   * 'connecting'), or null. A second `connect()` arriving mid-handshake
   * joins THIS promise instead of resolving early or opening a competing
   * socket — so a caller that awaits `connect()` never sees the socket as
   * ready before `onopen`. Set by `beginConnectAttempt()`, cleared when the
   * attempt settles.
   */
  private inFlightConnect: Promise<void> | null = null;
  /**
   * Monotonic id for the in-flight connection attempt. `doConnect()` stamps
   * its own generation on entry; `disconnect()` (and any superseding attempt)
   * advances it. After the async ws-ticket await, `doConnect()` checks
   * whether its generation is still current — if not, it aborts instead of
   * opening a socket nobody wants (the disconnect-during-ticket-fetch zombie).
   */
  private connectEpoch = 0;
  /**
   * The `reject` of the in-flight handshake's promise, while a socket is open
   * but not yet `onopen`. Lets `disconnect()` settle (reject) an awaited
   * `connect()` instead of leaving it to hang on a detached socket whose
   * `onclose` will never fire. Cleared when the attempt settles.
   */
  private settlePendingConnect: ((err: Error) => void) | null = null;
  /**
   * The set of requestIds this socket is currently subscribed to.
   *
   * Pre-multi-sub this was a single nullable `currentSubscription`; the
   * server-side `subscribe(connectionId, requestId)` replaced any
   * previous subscription, so concurrent in-flight ops (plugin
   * `image_generate` started while an internal-arche generation was in
   * flight, two plugin image_generates back-to-back, etc.) silently
   * clobbered each other's subscriptions and the older request's
   * `image_complete` broadcast was dropped. The Set lets a single
   * socket carry many subscriptions, and `unsubscribe(requestId)` tears
   * down exactly one without affecting the others. Re-subscribed on
   * reconnect (see doConnect).
   */
  private currentSubscriptions: Set<string> = new Set();
  private chatSubscribedChannels = new Set<string>();
  private reconnectCallbacks: Array<() => void> = [];

  // Wall-clock of the last inbound frame of ANY type. A consumer-side stall
  // watchdog reads this to tell "server quiet but alive" from "server dead":
  // combined with the server's periodic `heartbeat` frame, silence here means
  // the streaming process is actually gone, not just between tokens.
  private lastMessageAt = 0;

  // Event callbacks
  private tokenCallbacks: TokenCallback[] = [];
  private thinkingCallbacks: ThinkingCallback[] = [];
  private nodeCompleteCallbacks: NodeCompleteCallback[] = [];
  private completeCallbacks: RequestCompleteCallback[] = [];
  private errorCallbacks: ErrorCallback[] = [];
  private connectionChangeCallbacks: ConnectionChangeCallback[] = [];
  private subscriptionCallbacks: SubscriptionCallback[] = [];
  private progressCallbacks: ProgressCallback[] = [];
  private imageCompleteCallbacks: ImageCompleteCallback[] = [];
  private surfaceCompleteCallbacks: SurfaceCompleteCallback[] = [];
  private topicSubscribedCallbacks: TopicSubscribedCallback[] = [];
  private topicSubscribeDeniedCallbacks: TopicSubscribeDeniedCallback[] = [];
  private subscriptionEventCallbacks: SubscriptionEventCallback[] = [];
  private subscriptionExpiredCallbacks: SubscriptionExpiredCallback[] = [];
  private topicTerminatedCallbacks: TopicTerminatedCallback[] = [];
  private uiUpdateCallbacks: UiUpdateCallback[] = [];
  private settingsUpdateCallbacks: SettingsUpdateCallback[] = [];
  private filesUpdateCallbacks: FilesUpdateCallback[] = [];
  private systemPromptCallbacks: SystemPromptCallback[] = [];
  private activityCallbacks: ActivityCallback[] = [];
  private toolCallCallbacks: ToolCallCallback[] = [];
  private toolResultCallbacks: ToolResultCallback[] = [];
  private forgeScreenshotCallbacks: ForgeScreenshotCallback[] = [];
  private forgeQuestionCallbacks: ForgeQuestionCallback[] = [];
  private forgePlanCallbacks: ForgePlanCallback[] = [];
  private modelInfoCallbacks: ModelInfoCallback[] = [];
  private toolsAvailableCallbacks: ToolsAvailableCallback[] = [];
  private chatMessageCallbacks: ChatMessageCallback[] = [];
  private chatMessageEditedCallbacks: ChatMessageEditedCallback[] = [];
  private chatMessageDeletedCallbacks: ChatMessageDeletedCallback[] = [];
  private chatTypingCallbacks: ChatTypingCallback[] = [];
  private chatReactionCallbacks: ChatReactionCallback[] = [];
  private chatPresenceCallbacks: ChatPresenceCallback[] = [];
  private chatChannelAddedCallbacks: ChatChannelAddedCallback[] = [];
  private calendarSyncAppliedCallbacks: CalendarSyncAppliedCallback[] = [];
  private searchResultsCallbacks: SearchResultsCallback[] = [];
  private archeProposalCallbacks: ArcheProposalCallback[] = [];
  private assistantToolCallCallbacks: AssistantToolCallCallback[] = [];
  private assistantQuestionCallbacks: AssistantQuestionCallback[] = [];
  private assistantConfirmationCallbacks: AssistantConfirmationCallback[] = [];
  private assistantUiActionCallbacks: AssistantUiActionCallback[] = [];
  private assistantNavigateCallbacks: AssistantNavigateCallback[] = [];

  constructor(config: WebSocketConfig) {
    this.config = {
      ...config,
      maxReconnectAttempts: config.maxReconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS,
      reconnectDelayMs: config.reconnectDelayMs ?? DEFAULT_RECONNECT_DELAY_MS,
      heartbeatIntervalMs: config.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS,
    };
  }

  /**
   * Gets the current connection state
   */
  getState(): ConnectionState {
    return this.state;
  }

  /**
   * Checks if connected
   */
  isConnected(): boolean {
    return this.state === 'connected';
  }

  /**
   * Wall-clock (ms) of the last inbound frame of any type, or 0 if none yet.
   * Consumed by stall watchdogs to distinguish a quiet-but-alive stream from a
   * dead one.
   */
  getLastMessageAt(): number {
    return this.lastMessageAt;
  }

  /**
   * Gets the FIRST current subscription (back-compat).
   *
   * Old callers assumed at-most-one subscription; this still returns
   * exactly that for the single-subscription case. New callers that
   * need to introspect all subscriptions should use
   * `getCurrentSubscriptions()`.
   */
  getCurrentSubscription(): string | null {
    const first = this.currentSubscriptions.values().next();
    return first.done ? null : first.value;
  }

  /**
   * Returns a snapshot of all requestIds this socket is subscribed to.
   */
  getCurrentSubscriptions(): string[] {
    return Array.from(this.currentSubscriptions);
  }

  /**
   * Connects to the WebSocket server.
   *
   * The provider is invoked on every (re)connect and must return a
   * fresh single-use ws-ticket from `authApi.getWebSocketTicket()`.
   * The ticket is sent as `?ticket=` on the WebSocket URL.
   */
  async connect(tokenProvider: () => Promise<string | null>): Promise<void> {
    // Already live — nothing to do.
    if (this.state === 'connected') {
      return;
    }
    // A handshake is already in flight — JOIN it rather than resolving early
    // or opening a competing socket. Returning the in-flight promise means an
    // awaiting caller (e.g. chat readiness) only proceeds once the socket is
    // actually open. (A timer-driven reconnect populates inFlightConnect too;
    // the `?? undefined` keeps us safe if a handshake is somehow untracked.)
    if (this.state === 'connecting') {
      return this.inFlightConnect ?? undefined;
    }

    this.tokenProvider = tokenProvider;

    // If we're mid-backoff after an abnormal close (`state === 'reconnecting'`),
    // an explicit connect() means "try now". Cancel the pending reconnect
    // timer BEFORE dialing — otherwise its deferred doConnect() would fire a
    // SECOND socket alongside the one we're about to open, the two would race,
    // and the promise the caller awaited could hang when the other socket wins
    // the onopen path. stopReconnect() also resets reconnectAttempts, so this
    // covers the `disconnected` path too (a no-op timer-clear there).
    const wasReconnecting = this.state === 'reconnecting';
    this.stopReconnect();

    try {
      await this.beginConnectAttempt();
    } catch (err) {
      // We just pre-empted an in-flight backoff. If this forced attempt failed
      // BEFORE a socket existed (bad/transient ws-ticket → no onclose to
      // recover the chain) we must resume background reconnect ourselves —
      // otherwise a momentary ticket-endpoint blip would permanently kill
      // auto-reconnect. A socket-level failure already re-armed backoff via
      // onclose (state === 'reconnecting'); a supersede is an intentional
      // takeover — neither should re-arm here.
      if (
        wasReconnecting &&
        this.state !== 'reconnecting' &&
        !(err instanceof ConnectionSupersededError)
      ) {
        this.scheduleReconnect();
      }
      throw err;
    }
  }

  /**
   * Starts one connection attempt and publishes its promise as
   * `inFlightConnect` so a concurrent `connect()` can join it. The handle is
   * cleared when the attempt settles (resolve OR reject), guarded by identity
   * so a stale settle can't clobber a newer attempt's handle. Used by both
   * `connect()` and the reconnect timer.
   */
  private beginConnectAttempt(): Promise<void> {
    const attempt = this.doConnect();
    this.inFlightConnect = attempt;
    const clear = () => {
      if (this.inFlightConnect === attempt) this.inFlightConnect = null;
    };
    attempt.then(clear, clear);
    return attempt;
  }

  /**
   * Subscribes to streaming updates for a request. ADDITIVE — calling
   * `subscribe(B)` while already subscribed to A keeps both live. Each
   * call sends a `subscribe` message to the server, which adds to its
   * connection-level Set. Idempotent: subscribing twice to the same
   * requestId is a no-op (avoids redundant server work and a doubled
   * `subscribed` ack).
   *
   * Safe to call before the socket is connected: the requestId is added
   * to `currentSubscriptions` up-front so that when the `onopen` handler
   * runs its resubscribe loop it sees the pending entry and dispatches
   * the `subscribe` frame. This removes a race where the React-side
   * "connectionState → useEffect → subscribe" hop dropped the call
   * because the state update wasn't observed in time.
   */
  subscribe(requestId: string): void {
    if (this.currentSubscriptions.has(requestId)) return;
    this.currentSubscriptions.add(requestId);
    if (this.state !== 'connected') {
      return;
    }
    this.send({ type: 'subscribe', requestId });
  }

  /**
   * Unsubscribes from streaming updates.
   *
   *   unsubscribe(requestId)
   *     Drops just that one subscription; other in-flight subscriptions
   *     stay live. This is what concurrent callers should use.
   *
   *   unsubscribe()
   *     Clears ALL subscriptions on the socket. Retained for back-compat
   *     with legacy single-sub call sites and explicit "tear down
   *     everything" intent (e.g. page navigation cleanup).
   */
  unsubscribe(requestId?: string): void {
    if (this.state !== 'connected') {
      if (requestId !== undefined) this.currentSubscriptions.delete(requestId);
      else this.currentSubscriptions.clear();
      return;
    }
    if (requestId !== undefined) {
      if (!this.currentSubscriptions.has(requestId)) return;
      this.currentSubscriptions.delete(requestId);
      this.send({ type: 'unsubscribe', requestId });
    } else {
      this.currentSubscriptions.clear();
      this.send({ type: 'unsubscribe' });
    }
  }

  /**
   * Disconnects from the WebSocket server
   */
  disconnect(): void {
    // Supersede any in-flight attempt: an attempt still awaiting its ticket
    // will see the advanced epoch and abort instead of opening a socket after
    // we've torn down.
    this.connectEpoch++;
    this.stopReconnect();
    this.stopHeartbeat();
    this.currentSubscriptions.clear();

    if (this.socket) {
      // Detach handlers BEFORE closing: the close is asynchronous in a real
      // browser, so a late onclose would otherwise run after we've nulled the
      // socket — re-arming reconnect, or worse, nulling a socket a subsequent
      // connect() has already opened. Detaching makes the close inert.
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.close(1000, 'Client disconnect');
      this.socket = null;
    }

    // Settle an awaited connect() whose handshake we just tore down — the
    // detached socket's onclose will never fire, so without this the promise
    // would hang forever.
    if (this.settlePendingConnect) {
      const rejectInFlight = this.settlePendingConnect;
      this.settlePendingConnect = null;
      rejectInFlight(new Error(WS_DISCONNECTED_BEFORE_CONNECTING));
    }

    this.setState('disconnected');
  }

  /**
   * Registers a callback for token events
   */
  onToken(callback: TokenCallback): () => void {
    this.tokenCallbacks.push(callback);
    return () => {
      this.tokenCallbacks = this.tokenCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for thinking events (extended thinking from models like Claude)
   */
  onThinking(callback: ThinkingCallback): () => void {
    this.thinkingCallbacks.push(callback);
    return () => {
      this.thinkingCallbacks = this.thinkingCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for node complete events
   */
  onNodeComplete(callback: NodeCompleteCallback): () => void {
    this.nodeCompleteCallbacks.push(callback);
    return () => {
      this.nodeCompleteCallbacks = this.nodeCompleteCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for request complete events
   */
  onComplete(callback: RequestCompleteCallback): () => void {
    this.completeCallbacks.push(callback);
    return () => {
      this.completeCallbacks = this.completeCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for error events
   */
  onError(callback: ErrorCallback): () => void {
    this.errorCallbacks.push(callback);
    return () => {
      this.errorCallbacks = this.errorCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for connection state changes
   */
  onConnectionChange(callback: ConnectionChangeCallback): () => void {
    this.connectionChangeCallbacks.push(callback);
    return () => {
      this.connectionChangeCallbacks = this.connectionChangeCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  /**
   * Registers a callback for subscription confirmations
   */
  onSubscribed(callback: SubscriptionCallback): () => void {
    this.subscriptionCallbacks.push(callback);
    return () => {
      this.subscriptionCallbacks = this.subscriptionCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for progress events (used by image generation)
   */
  onProgress(callback: ProgressCallback): () => void {
    this.progressCallbacks.push(callback);
    return () => {
      this.progressCallbacks = this.progressCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for image complete events
   */
  onImageComplete(callback: ImageCompleteCallback): () => void {
    this.imageCompleteCallbacks.push(callback);
    return () => {
      this.imageCompleteCallbacks = this.imageCompleteCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for generic async-surface completion events
   * (`surface_invoke` with `asyncDispatch`). The plugin bridge host filters
   * by requestId and resolves the in-flight surface call with `data.result`.
   */
  onSurfaceComplete(callback: SurfaceCompleteCallback): () => void {
    this.surfaceCompleteCallbacks.push(callback);
    return () => {
      this.surfaceCompleteCallbacks = this.surfaceCompleteCallbacks.filter((cb) => cb !== callback);
    };
  }

  // ==========================================================================
  // Realtime topic subscriptions (ADR-012 Phase M4)
  // ==========================================================================
  //
  // Topics are DELIBERATELY not tracked in `currentSubscriptions`: the
  // onopen resubscribe loop blind-resends requestIds, but a topic
  // subscription is admitted by a SINGLE-USE grant — a blind replay would
  // present a consumed grant and be denied on every reconnect. Topic
  // re-establishment is the plugin subscription manager's job (via
  // `onReconnect`): re-run the subscribe verb, get a fresh grant, subscribe
  // again. This also re-authorizes — which is the design, not a cost.

  /** Presents a single-use grant to establish a lease-bounded topic subscription. */
  topicSubscribe(topic: string, grant: string): void {
    this.send({ type: 'topic_subscribe', topic, grant });
  }

  /** Tears down the caller's own topic subscription (no credential needed). */
  topicUnsubscribe(topic: string): void {
    this.send({ type: 'topic_unsubscribe', topic });
  }

  onTopicSubscribed(callback: TopicSubscribedCallback): () => void {
    this.topicSubscribedCallbacks.push(callback);
    return () => {
      this.topicSubscribedCallbacks = this.topicSubscribedCallbacks.filter((cb) => cb !== callback);
    };
  }

  onTopicSubscribeDenied(callback: TopicSubscribeDeniedCallback): () => void {
    this.topicSubscribeDeniedCallbacks.push(callback);
    return () => {
      this.topicSubscribeDeniedCallbacks = this.topicSubscribeDeniedCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  onSubscriptionEvent(callback: SubscriptionEventCallback): () => void {
    this.subscriptionEventCallbacks.push(callback);
    return () => {
      this.subscriptionEventCallbacks = this.subscriptionEventCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  onSubscriptionExpired(callback: SubscriptionExpiredCallback): () => void {
    this.subscriptionExpiredCallbacks.push(callback);
    return () => {
      this.subscriptionExpiredCallbacks = this.subscriptionExpiredCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  onTopicTerminated(callback: TopicTerminatedCallback): () => void {
    this.topicTerminatedCallbacks.push(callback);
    return () => {
      this.topicTerminatedCallbacks = this.topicTerminatedCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for UI definition updates (from builder tools)
   */
  onUiUpdate(callback: UiUpdateCallback): () => void {
    this.uiUpdateCallbacks.push(callback);
    return () => {
      this.uiUpdateCallbacks = this.uiUpdateCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for settings updates (e.g. guided phase changes)
   */
  onSettingsUpdate(callback: SettingsUpdateCallback): () => void {
    this.settingsUpdateCallbacks.push(callback);
    return () => {
      this.settingsUpdateCallbacks = this.settingsUpdateCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for file updates (from plugin builder tools)
   */
  onFilesUpdate(callback: FilesUpdateCallback): () => void {
    this.filesUpdateCallbacks.push(callback);
    return () => {
      this.filesUpdateCallbacks = this.filesUpdateCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for system prompt events (verbose mode)
   */
  onSystemPrompt(callback: SystemPromptCallback): () => void {
    this.systemPromptCallbacks.push(callback);
    return () => {
      this.systemPromptCallbacks = this.systemPromptCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for the always-on activity status line ("Writing
   * code…") shown while the assistant works through tool calls.
   */
  onActivity(callback: ActivityCallback): () => void {
    this.activityCallbacks.push(callback);
    return () => {
      this.activityCallbacks = this.activityCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for tool call events (verbose mode)
   */
  onToolCall(callback: ToolCallCallback): () => void {
    this.toolCallCallbacks.push(callback);
    return () => {
      this.toolCallCallbacks = this.toolCallCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for tool result events (verbose mode)
   */
  onToolResult(callback: ToolResultCallback): () => void {
    this.toolResultCallbacks.push(callback);
    return () => {
      this.toolResultCallbacks = this.toolResultCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for Fias Forge screenshot frames.
   */
  onForgeScreenshot(callback: ForgeScreenshotCallback): () => void {
    this.forgeScreenshotCallbacks.push(callback);
    return () => {
      this.forgeScreenshotCallbacks = this.forgeScreenshotCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for Fias Forge question frames (Claude is waiting on the owner).
   */
  onForgeQuestion(callback: ForgeQuestionCallback): () => void {
    this.forgeQuestionCallbacks.push(callback);
    return () => {
      this.forgeQuestionCallbacks = this.forgeQuestionCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for Fias Forge plan frames (Claude is waiting on the owner's approval).
   */
  onForgePlan(callback: ForgePlanCallback): () => void {
    this.forgePlanCallbacks.push(callback);
    return () => {
      this.forgePlanCallbacks = this.forgePlanCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for model info events (verbose mode)
   */
  onModelInfo(callback: ModelInfoCallback): () => void {
    this.modelInfoCallbacks.push(callback);
    return () => {
      this.modelInfoCallbacks = this.modelInfoCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for tools available events (verbose mode)
   */
  onToolsAvailable(callback: ToolsAvailableCallback): () => void {
    this.toolsAvailableCallbacks.push(callback);
    return () => {
      this.toolsAvailableCallbacks = this.toolsAvailableCallbacks.filter((cb) => cb !== callback);
    };
  }

  // ==========================================================================
  // Chat Methods
  // ==========================================================================

  /**
   * Subscribes to chat channels for real-time updates
   */
  subscribeToChatChannels(channelIds: string[]): void {
    if (this.state !== 'connected') {
      console.warn('Cannot subscribe to chat channels: WebSocket not connected');
      return;
    }
    channelIds.forEach((id) => this.chatSubscribedChannels.add(id));
    this.send({ type: 'chat_subscribe', channelIds });
  }

  /**
   * Unsubscribes from chat channels
   */
  unsubscribeFromChatChannels(channelIds: string[]): void {
    channelIds.forEach((id) => this.chatSubscribedChannels.delete(id));
    if (this.state !== 'connected') return;
    this.send({ type: 'chat_unsubscribe', channelIds });
  }

  /**
   * Registers a callback invoked after a WebSocket reconnection completes.
   */
  onReconnect(callback: () => void): () => void {
    this.reconnectCallbacks.push(callback);
    return () => {
      this.reconnectCallbacks = this.reconnectCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Sends a typing indicator for a chat channel
   */
  sendChatTyping(channelId: string): void {
    if (this.state !== 'connected') return;
    this.send({ type: 'chat_typing', channelId });
  }

  /**
   * Sends a read receipt for a chat channel
   */
  sendChatRead(channelId: string, messageId: string): void {
    if (this.state !== 'connected') return;
    this.send({ type: 'chat_read', channelId, messageId });
  }

  /**
   * Registers a callback for new chat messages
   */
  onChatMessage(callback: ChatMessageCallback): () => void {
    this.chatMessageCallbacks.push(callback);
    return () => {
      this.chatMessageCallbacks = this.chatMessageCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for edited chat messages
   */
  onChatMessageEdited(callback: ChatMessageEditedCallback): () => void {
    this.chatMessageEditedCallbacks.push(callback);
    return () => {
      this.chatMessageEditedCallbacks = this.chatMessageEditedCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  /**
   * Registers a callback for deleted chat messages
   */
  onChatMessageDeleted(callback: ChatMessageDeletedCallback): () => void {
    this.chatMessageDeletedCallbacks.push(callback);
    return () => {
      this.chatMessageDeletedCallbacks = this.chatMessageDeletedCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  /**
   * Registers a callback for typing indicators
   */
  onChatTyping(callback: ChatTypingCallback): () => void {
    this.chatTypingCallbacks.push(callback);
    return () => {
      this.chatTypingCallbacks = this.chatTypingCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for reaction changes
   */
  onChatReaction(callback: ChatReactionCallback): () => void {
    this.chatReactionCallbacks.push(callback);
    return () => {
      this.chatReactionCallbacks = this.chatReactionCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for presence changes
   */
  onChatPresence(callback: ChatPresenceCallback): () => void {
    this.chatPresenceCallbacks.push(callback);
    return () => {
      this.chatPresenceCallbacks = this.chatPresenceCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for when the user is added to a new channel
   */
  onChatChannelAdded(callback: ChatChannelAddedCallback): () => void {
    this.chatChannelAddedCallbacks.push(callback);
    return () => {
      this.chatChannelAddedCallbacks = this.chatChannelAddedCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  /**
   * Registers a callback for calendar provider-sync updates (remote changes
   * were applied to the given local calendars — refetch them).
   */
  onCalendarSyncApplied(callback: CalendarSyncAppliedCallback): () => void {
    this.calendarSyncAppliedCallbacks.push(callback);
    return () => {
      this.calendarSyncAppliedCallbacks = this.calendarSyncAppliedCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  /**
   * Registers a callback for Fias AI search results
   */
  onSearchResults(callback: SearchResultsCallback): () => void {
    this.searchResultsCallbacks.push(callback);
    return () => {
      this.searchResultsCallbacks = this.searchResultsCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Registers a callback for Fias AI arche proposals
   */
  onArcheProposal(callback: ArcheProposalCallback): () => void {
    this.archeProposalCallbacks.push(callback);
    return () => {
      this.archeProposalCallbacks = this.archeProposalCallbacks.filter((cb) => cb !== callback);
    };
  }

  /** Assistant agent-loop events (activity feed / question / confirmation / ui-action) */
  onAssistantToolCall(callback: AssistantToolCallCallback): () => void {
    this.assistantToolCallCallbacks.push(callback);
    return () => {
      this.assistantToolCallCallbacks = this.assistantToolCallCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  onAssistantQuestion(callback: AssistantQuestionCallback): () => void {
    this.assistantQuestionCallbacks.push(callback);
    return () => {
      this.assistantQuestionCallbacks = this.assistantQuestionCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  onAssistantConfirmation(callback: AssistantConfirmationCallback): () => void {
    this.assistantConfirmationCallbacks.push(callback);
    return () => {
      this.assistantConfirmationCallbacks = this.assistantConfirmationCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  onAssistantUiAction(callback: AssistantUiActionCallback): () => void {
    this.assistantUiActionCallbacks.push(callback);
    return () => {
      this.assistantUiActionCallbacks = this.assistantUiActionCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  onAssistantNavigate(callback: AssistantNavigateCallback): () => void {
    this.assistantNavigateCallbacks.push(callback);
    return () => {
      this.assistantNavigateCallbacks = this.assistantNavigateCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  // Private methods

  private async doConnect(): Promise<void> {
    // Stamp this attempt's generation. A disconnect() (or a superseding
    // attempt) advances connectEpoch; after the async ticket await below we
    // check it to abort an attempt nobody wants anymore.
    const epoch = ++this.connectEpoch;
    this.setState('connecting');

    // Invariant: at most one live socket. If a prior socket somehow survived
    // (defensive — the reconnect path nulls it in onclose, and connect()
    // tears down the reconnect timer), detach its handlers first so the
    // close() below cannot re-enter scheduleReconnect, then drop it.
    if (this.socket) {
      const stale = this.socket;
      this.socket = null;
      stale.onopen = null;
      stale.onclose = null;
      stale.onerror = null;
      stale.onmessage = null;
      stale.close(1000, 'Superseded by new connection');
    }

    // Mint a fresh single-use ticket. Required on every (re)connect
    // because the previous ticket has been consumed by the prior
    // connect attempt and cannot be replayed.
    if (!this.tokenProvider) {
      this.setState('disconnected');
      throw new Error('No token provider configured — call connect() first');
    }
    let ticket: string | null;
    try {
      ticket = await this.tokenProvider();
    } catch (err) {
      // Superseded while awaiting the ticket (e.g. disconnect()) — the takeover
      // owns connection state now; abort without clobbering it.
      if (epoch !== this.connectEpoch) throw new ConnectionSupersededError();
      // The provider may throw for non-auth failures (e.g. 5xx from the
      // ws-ticket endpoint). Reset state so a follow-up connect() isn't
      // blocked by `state === 'connecting'`, then re-throw verbatim —
      // useStreaming relies on the original message to decide whether
      // this is a session-expired event vs. a transient outage.
      this.setState('disconnected');
      throw err;
    }
    // A disconnect() (or newer attempt) landed during the ticket fetch — don't
    // open a socket nobody asked for (the post-disconnect zombie connection).
    if (epoch !== this.connectEpoch) {
      throw new ConnectionSupersededError();
    }
    if (!ticket) {
      this.setState('disconnected');
      throw new Error('Authentication credential unavailable');
    }

    return new Promise((resolve, reject) => {
      const wsUrl = `${this.config.endpoint}?ticket=${encodeURIComponent(ticket)}`;

      // Settle this promise exactly once. Guarantees the awaited connect()
      // never hangs: it resolves when the socket opens, and rejects on the
      // FIRST terminal signal of this attempt (onerror, or an abnormal close
      // that arrives without a preceding onerror). Background auto-reconnect
      // is a separate concern handled in onclose and is unaffected by this.
      let settled = false;
      const settleResolve = () => {
        if (settled) return;
        settled = true;
        if (this.settlePendingConnect === settleReject) this.settlePendingConnect = null;
        resolve();
      };
      const settleReject = (err: Error) => {
        if (settled) return;
        settled = true;
        if (this.settlePendingConnect === settleReject) this.settlePendingConnect = null;
        reject(err);
      };
      // Expose this attempt's reject so disconnect() can settle an awaited
      // connect() that is past the ticket fetch but not yet open — without it,
      // disconnect() detaches the socket's handlers and the promise would hang.
      this.settlePendingConnect = settleReject;

      try {
        this.socket = new WebSocket(wsUrl);

        this.socket.onopen = () => {
          this.setState('connected');
          // A socket is live — tear down all reconnect bookkeeping so no
          // orphaned backoff timer lingers (the timer that scheduled this
          // very doConnect has already fired; its handle is stale) and the
          // next abnormal close starts its backoff from attempt 0.
          this.stopReconnect();
          this.startHeartbeat();

          // Re-subscribe to ALL requestIds we had subscribed before the
          // disconnect. Snapshot+clear+re-add via subscribe() so the
          // idempotent "already subscribed" guard short-circuit clears
          // first (otherwise the Set would still hold them and
          // subscribe() would no-op without sending to the server).
          const toResubscribe = Array.from(this.currentSubscriptions);
          this.currentSubscriptions.clear();
          for (const reqId of toResubscribe) {
            this.subscribe(reqId);
          }

          // Re-subscribe to chat channels after reconnect
          if (this.chatSubscribedChannels.size > 0) {
            this.send({
              type: 'chat_subscribe',
              channelIds: Array.from(this.chatSubscribedChannels),
            });
          }

          // Notify reconnect listeners
          this.reconnectCallbacks.forEach((cb) => cb());

          settleResolve();
        };

        this.socket.onclose = (event) => {
          // Was this socket still mid-handshake? Capture before we mutate
          // state so a close that arrives without a preceding onerror still
          // rejects the awaited promise instead of leaving it pending.
          const closedWhileConnecting = this.state === 'connecting';
          this.socket = null;
          this.stopHeartbeat();

          if (event.code === 1000) {
            // Normal close
            this.setState('disconnected');
          } else {
            // Abnormal close - try to reconnect
            this.scheduleReconnect();
          }

          if (closedWhileConnecting) {
            settleReject(new Error('WebSocket closed before connecting'));
          }
        };

        this.socket.onerror = (error) => {
          console.error('WebSocket error:', error);
          if (this.state === 'connecting') {
            settleReject(new Error('Failed to connect to WebSocket'));
          }
        };

        this.socket.onmessage = (event) => {
          this.handleMessage(event.data);
        };
      } catch (error) {
        this.setState('disconnected');
        settleReject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data) as ServerMessage;

      // Any inbound frame is proof of life — record it before dispatch so a
      // stall watchdog measures true silence regardless of frame type.
      this.lastMessageAt = Date.now();

      // Server liveness heartbeat: its only job is to bump lastMessageAt (above)
      // during long-but-alive gaps (e.g. provider time-to-first-token, a slow
      // server-side build). No payload; return before the switch so it doesn't
      // fall through to the unknown-type warning.
      if (message.type === 'heartbeat') return;

      switch (message.type) {
        case 'token':
          this.tokenCallbacks.forEach((cb) => cb(message.nodeId, message.text, message.requestId));
          break;

        case 'thinking':
          this.thinkingCallbacks.forEach((cb) =>
            cb(message.nodeId, message.text, message.requestId),
          );
          break;

        case 'node_complete':
          this.nodeCompleteCallbacks.forEach((cb) =>
            cb(message.nodeId, message.requestId, message.costCredits),
          );
          break;

        case 'complete': {
          // Extract extra payload fields (everything except type/requestId)
          const { type: _type, requestId: completedId, ...payload } = message;
          const extra =
            Object.keys(payload).length > 0 ? (payload as Record<string, unknown>) : undefined;
          this.completeCallbacks.forEach((cb) => cb(completedId, extra));
          break;
        }

        case 'error':
          this.errorCallbacks.forEach((cb) => cb(message.code, message.message, message.requestId));
          break;

        case 'subscribed':
          this.subscriptionCallbacks.forEach((cb) => cb(message.requestId));
          break;

        case 'unsubscribed':
          // No callbacks needed for unsubscribe confirmation
          break;

        case 'pong':
          // Heartbeat response - no action needed
          break;

        case 'progress':
          this.progressCallbacks.forEach((cb) =>
            cb(message.stage, message.message, message.requestId),
          );
          break;

        case 'image_complete':
          this.imageCompleteCallbacks.forEach((cb) =>
            cb({
              requestId: message.requestId,
              imageRef: message.imageRef,
              fileId: message.fileId,
              revisedPrompt: message.revisedPrompt,
              provider: message.provider,
              model: message.model,
              imageUrl: message.imageUrl,
              costCents: message.costCents,
              savedToPersonal: message.savedToPersonal,
              deprecation: message.deprecation,
            }),
          );
          break;

        case 'surface_complete':
          this.surfaceCompleteCallbacks.forEach((cb) =>
            cb({ requestId: message.requestId, result: message.result }),
          );
          break;

        case 'ui_update':
          this.uiUpdateCallbacks.forEach((cb) =>
            cb(message.nodeId, message.uiDefinition, message.requestId),
          );
          break;

        case 'settings_update':
          this.settingsUpdateCallbacks.forEach((cb) => cb(message.settings, message.requestId));
          break;

        case 'files_update':
          this.filesUpdateCallbacks.forEach((cb) => cb(message.files, message.requestId));
          break;

        case 'system_prompt':
          this.systemPromptCallbacks.forEach((cb) => cb(message.prompt, message.requestId));
          break;

        case 'activity':
          this.activityCallbacks.forEach((cb) => cb(message.label, message.requestId));
          break;

        case 'tool_call':
          this.toolCallCallbacks.forEach((cb) =>
            cb(
              message.toolName ?? message.tool ?? '',
              message.toolInput ?? message.input,
              message.requestId,
              message.toolUseId,
            ),
          );
          break;

        case 'tool_result':
          this.toolResultCallbacks.forEach((cb) =>
            cb(
              message.toolName ?? '',
              message.toolResult ?? message.output,
              message.isError,
              message.requestId,
              message.toolUseId,
            ),
          );
          break;

        case 'forge_screenshot':
          this.forgeScreenshotCallbacks.forEach((cb) =>
            cb({
              requestId: message.requestId,
              path: message.path,
              contentBase64: message.contentBase64,
            }),
          );
          break;

        case 'forge_question':
          this.forgeQuestionCallbacks.forEach((cb) =>
            cb({
              requestId: message.requestId,
              turnId: message.turnId,
              questionId: message.questionId,
              questions: message.questions,
            }),
          );
          break;

        case 'forge_plan':
          this.forgePlanCallbacks.forEach((cb) =>
            cb({
              requestId: message.requestId,
              turnId: message.turnId,
              questionId: message.questionId,
              plan: message.plan,
            }),
          );
          break;

        case 'model_info':
          this.modelInfoCallbacks.forEach((cb) =>
            cb(message.model, message.intent, message.requestId),
          );
          break;

        case 'tools_available':
          this.toolsAvailableCallbacks.forEach((cb) => cb(message.tools, message.requestId));
          break;

        case 'chat_message':
          this.chatMessageCallbacks.forEach((cb) => cb(message.channelId, message.message));
          break;

        case 'chat_message_edited':
          this.chatMessageEditedCallbacks.forEach((cb) =>
            cb(message.channelId, message.messageId, message.content, message.editedAt),
          );
          break;

        case 'chat_message_deleted':
          this.chatMessageDeletedCallbacks.forEach((cb) =>
            cb(message.channelId, message.messageId),
          );
          break;

        case 'chat_typing':
          this.chatTypingCallbacks.forEach((cb) =>
            cb(message.channelId, message.userId, message.displayName),
          );
          break;

        case 'chat_reaction':
          this.chatReactionCallbacks.forEach((cb) =>
            cb(message.channelId, message.messageId, message.userId, message.emoji, message.action),
          );
          break;

        case 'chat_presence':
          this.chatPresenceCallbacks.forEach((cb) => cb(message.userId, message.status));
          break;

        case 'chat_channel_added':
          this.chatChannelAddedCallbacks.forEach((cb) => cb(message.channelId));
          break;

        case 'calendar-sync-applied':
          this.calendarSyncAppliedCallbacks.forEach((cb) => cb(message.calendarIds));
          break;

        case 'chat_subscribed':
          // Acknowledgment of chat channel subscription
          break;

        case 'topic_subscribed':
          this.topicSubscribedCallbacks.forEach((cb) => cb(message.topic, message.leaseExpiresAt));
          break;

        case 'topic_subscribe_denied':
          this.topicSubscribeDeniedCallbacks.forEach((cb) => cb(message.topic, message.code));
          break;

        case 'subscription_event':
          this.subscriptionEventCallbacks.forEach((cb) => cb(message.topic, message.event));
          break;

        case 'subscription_expired':
          this.subscriptionExpiredCallbacks.forEach((cb) => cb(message.topic));
          break;

        case 'topic_terminated':
          this.topicTerminatedCallbacks.forEach((cb) => cb(message.topic));
          break;

        case 'search_results':
          this.searchResultsCallbacks.forEach((cb) =>
            cb(message.results, message.query, message.requestId),
          );
          break;

        case 'arche_proposal':
          this.archeProposalCallbacks.forEach((cb) =>
            cb({ proposalId: message.proposalId, actions: message.actions }, message.requestId),
          );
          break;

        case 'assistant_tool_call':
          this.assistantToolCallCallbacks.forEach((cb) => cb(message));
          break;

        case 'assistant_question':
          this.assistantQuestionCallbacks.forEach((cb) => cb(message));
          break;

        case 'assistant_confirmation_required':
          this.assistantConfirmationCallbacks.forEach((cb) => cb(message));
          break;

        case 'assistant_ui_action':
          this.assistantUiActionCallbacks.forEach((cb) => cb(message));
          break;

        case 'assistant_navigate':
          this.assistantNavigateCallbacks.forEach((cb) => cb(message));
          break;

        default:
          console.warn('Unknown WebSocket message type:', (message as { type: string }).type);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }

  private send(message: ClientMessage): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  private setState(state: ConnectionState): void {
    if (this.state !== state) {
      this.state = state;
      this.connectionChangeCallbacks.forEach((cb) => cb(state));
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping' });
    }, this.config.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    // Never stack reconnect timers. An abnormal close fires onclose
    // (→ here), and a failed reconnect attempt's catch also lands here; if
    // both raced we'd arm two timers and later open two sockets. Clear any
    // pending one first so exactly one backoff is ever in flight.
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Terminal already reported — don't fire CONNECTION_FAILED again when the
    // reconnect timer's catch re-enters after onclose already exhausted us.
    if (this.reconnectExhausted) {
      return;
    }

    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.reconnectExhausted = true;
      this.setState('disconnected');
      this.errorCallbacks.forEach((cb) =>
        cb('CONNECTION_FAILED', 'Failed to reconnect after maximum attempts'),
      );
      return;
    }

    this.setState('reconnecting');
    this.reconnectAttempts++;

    // Exponential backoff with jitter
    const delay = Math.min(
      this.config.reconnectDelayMs * Math.pow(2, this.reconnectAttempts - 1) + Math.random() * 1000,
      30000, // Max 30 seconds
    );

    console.log(`Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      // The timer has fired; drop its now-stale handle so the dedup guard
      // above (and disconnect()) reason about live timers only.
      this.reconnectTimer = null;
      // beginConnectAttempt() publishes inFlightConnect so a concurrent
      // connect() joins this attempt instead of racing a second socket.
      this.beginConnectAttempt().catch((error) => {
        // A supersede (disconnect() or an explicit connect() took over) is not
        // a failure — never re-arm backoff against an intentional takeover.
        if (error instanceof ConnectionSupersededError) {
          return;
        }
        console.error('Reconnection failed:', error);
        // doConnect rejected. If the failure was at the socket level, its
        // onclose has ALREADY re-armed the backoff (state === 'reconnecting')
        // — don't schedule a second, attempt-doubling timer. Only a
        // pre-socket failure (ticket fetch threw / returned null, ctor threw;
        // state left 'disconnected') has no onclose to recover it, so we
        // reschedule here.
        if (this.state !== 'reconnecting') {
          this.scheduleReconnect();
        }
      });
    }, delay);
  }

  private stopReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
    // Clear the terminal latch so a subsequent connect()/open can start a
    // fresh reconnect chain after a previous one was exhausted.
    this.reconnectExhausted = false;
  }
}

// Singleton instance with default config
let defaultInstance: FiasWebSocket | null = null;

/**
 * Gets or creates the default WebSocket instance
 * @param endpoint WebSocket endpoint URL
 */
export function getWebSocket(endpoint?: string): FiasWebSocket {
  if (!defaultInstance && endpoint) {
    defaultInstance = new FiasWebSocket({ endpoint });
  }
  if (!defaultInstance) {
    throw new Error('WebSocket not initialized. Call getWebSocket with endpoint first.');
  }
  return defaultInstance;
}

/**
 * Resets the default WebSocket instance
 * Used for testing or when switching environments
 */
export function resetWebSocket(): void {
  if (defaultInstance) {
    defaultInstance.disconnect();
    defaultInstance = null;
  }
}

export default FiasWebSocket;
